import { useCallback } from 'react';

import { type AnyDocument, type PartialDocument, Category, ContentParsingStatus } from '../../types';
import { isBookwise } from '../../utils/env.platform';
// eslint-disable-next-line import/no-cycle
import { globalState } from '../models';


// True if chunking is enabled for profile and document.
// False if chunking is disabled.
// Undefined if value is still indeterminate e.g. persistent state or doc still loading.
// Only returns true or false if it's absolutely certain that the doc is chunked or unchunked.
export function useIsChunkingEnabledForDoc(
  doc: PartialDocument<AnyDocument, 'id' | 'category'> | null,
): boolean | undefined {
  return globalState(useCallback((state) => getIsChunkingEnabledForDoc(doc, state), [doc]));
}

// True if chunking is enabled for profile and document.
// False if chunking is disabled.
// Undefined if persistent state or doc still loading.
export function getIsChunkingEnabledForDoc(
  doc: PartialDocument<AnyDocument, 'id' | 'category'> | null,
  state = globalState.getState(),
): boolean | undefined {
  if (!doc) {
    // if doc is still loading, do not return a definite value. doing so could cause issues in subsequent code.
    return undefined;
  }

  if (doc.category !== Category.EPUB) {
    return false;
  }

  const transientData = state.transientDocumentsData[doc.id];

  if (!transientData) {
    return undefined;
  }

  if ([ContentParsingStatus.Pending, ContentParsingStatus.ServerTaskNotStartedYet].includes(transientData.contentParsingStatus)) {
    // While content is still being parsed on the server, do not mark document as chunked or unchunked yet.
    return undefined;
  }

  if (!transientData.manifest) {
    // Docs without a manifest (and spine) are not chunked by definition
    return false;
  }

  if (isBookwise) {
    // as a failsafe, we consider everyone using Bookwise to have chunking enabled by default.
    return true;
  }

  // At this point, we know for certain the document is chunked. The only other requirement
  // is that the user is in the chunking enabled test group.

  return true;
}
